import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import AnimalAvatar from '../../components/AnimalAvatar';
import Icon from '../../components/Icon';
import useToast from '../../components/toast/useToast';
import { useIdentity } from '../../hooks/useIdentity';
import useLongPress from '../../hooks/useLongPress';
import useComposerPrefill from '../../hooks/useComposerPrefill';
import ReactionRow from './ReactionRow';
import ReactionPopover from './ReactionPopover';
import CodeBubble from '../codedrop/CodeBubble';
import FileCard from '../beam/FileCard';
import { useTransfers, transferStore } from '../../state/transfers';
import { MeshContext } from '../../providers/MeshProvider';
// Import ConfirmDialog directly (not via the user barrel) so Bubble doesn't
// drag UserModal + ProfileDrawer into the main chunk — those are lazy-loaded
// by MobileNav / DesktopSidebar and shouldn't ship eagerly.
import ConfirmDialog from '../user/ConfirmDialog';
import { toggleReaction } from './firestore';
import './Bubble.css';

// Format a Firestore Timestamp | Date | null into a short HH:mm string.
// While serverTimestamp() is in flight, `sentAt` is null — we render "just now"
// so the user gets feedback even before the round-trip resolves.
function formatTime(sentAt) {
  if (!sentAt) return 'just now';
  // Firestore Timestamp has a toDate() method; Date is already a Date.
  let d;
  if (typeof sentAt.toDate === 'function') {
    d = sentAt.toDate();
  } else if (sentAt instanceof Date) {
    d = sentAt;
  } else if (typeof sentAt === 'number') {
    d = new Date(sentAt);
  } else if (sentAt.seconds != null) {
    d = new Date(sentAt.seconds * 1000);
  } else {
    return 'just now';
  }
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Inner component that renders a FileCard for an `attachment.kind === 'file'`.
 *
 * Lookup strategy:
 *   1. Query the in-memory transferStore by transferId. If a live record
 *      exists (the recipient is connected and is actively receiving, or
 *      the sender is mid-transfer), render it with pause/resume/cancel
 *      wired through MeshContext (when available).
 *   2. Otherwise, render a static "done" card built from the attachment's
 *      historical fields. Late-joiners see this — they never saw the
 *      offer envelope, so there's no live progress to attach to. Download
 *      affordance is disabled today; F11.4's cloud-URL wiring will swap
 *      this for an actual fetch-from-Storage flow.
 */
const FileAttachment = ({ attachment, currentUserId }) => {
  // Subscribe scoped to this transferId — keeps the hook quiet when other
  // transfers churn.
  const matches = useTransfers((t) => t.id === attachment.transferId);
  const liveTransfer = matches[0] || null;

  // MeshContext may legitimately be null in tests / standalone surfaces;
  // we fall back to no-op handlers in that case so the card still renders.
  const mesh = useContext(MeshContext);

  // S8 — a cloud (internet) file attachment carries `via: 'cloud'` and a
  // `url` pointing at Supabase Storage. There is no WebRTC offer/transfer for
  // these, so every recipient (and the sender on reload) renders the synthetic
  // path below; the URL is the download affordance. LAN/Beam attachments have
  // no `url` and stay on the `webrtc` path as before.
  const isCloudAttachment =
    attachment.via === 'cloud' && typeof attachment.url === 'string' && attachment.url.length > 0;

  const synthetic = useMemo(() => {
    if (liveTransfer) return null;
    return {
      id: attachment.transferId,
      kind: 'file',
      // Cloud files keep `via: 'cloud'` so FileCard shows the Cloud chip and a
      // download link; LAN files remain `webrtc` (post-F11.4-cancel).
      via: isCloudAttachment ? 'cloud' : 'webrtc',
      name: attachment.name,
      size: Number(attachment.size) || 0,
      mime: attachment.mime || 'application/octet-stream',
      fromId: attachment.fromId,
      toId: currentUserId,
      progress: 100,
      state: 'done',
      bytesSent: Number(attachment.size) || 0,
      // FileCard renders a real download anchor when a cloud transfer has a URL.
      downloadUrl: isCloudAttachment ? attachment.url : undefined,
      startedAt: 0,
      lastTickAt: 0,
      lastTickBytes: 0,
      speedMBps: 0,
    };
  }, [liveTransfer, attachment, currentUserId, isCloudAttachment]);

  const transfer = liveTransfer || synthetic;
  if (!transfer) return null;

  // Only live transfers in flight get control affordances. A historical
  // synthetic card is intentionally read-only. For S8 cloud (internet) file
  // attachments the synthetic card carries `via:'cloud'` + a `downloadUrl`,
  // and FileCard renders a real download anchor — no live handle needed.
  const isLive = !!liveTransfer && liveTransfer.state !== 'done' && liveTransfer.state !== 'failed';

  const onPause = isLive && mesh && typeof mesh.sendCodeEnvelope === 'function'
    ? () => {
        // We reuse the mesh.send pathway via `mesh.mesh.send` since
        // sendCodeEnvelope is namespaced to `code` envelopes. The actual
        // mesh instance is exposed via the context value.
        try {
          if (mesh.mesh) mesh.mesh.send({ kind: 'file-pause', id: transfer.id }, transfer.toId);
          transferStore.update(transfer.id, { state: 'paused' });
        } catch { /* best effort */ }
      }
    : undefined;
  const onResume = isLive && mesh
    ? () => {
        try {
          if (mesh.mesh) mesh.mesh.send({ kind: 'file-resume', id: transfer.id }, transfer.toId);
          transferStore.update(transfer.id, { state: 'active' });
        } catch { /* best effort */ }
      }
    : undefined;
  const onCancel = isLive && mesh
    ? () => {
        try {
          if (mesh.mesh) mesh.mesh.send({ kind: 'file-abort', id: transfer.id }, transfer.toId);
          transferStore.update(transfer.id, { state: 'failed' });
        } catch { /* best effort */ }
      }
    : undefined;

  return (
    <FileCard
      transfer={transfer}
      onPause={onPause}
      onResume={onResume}
      onCancel={onCancel}
    />
  );
};

/**
 * Bubble — a single message row. The `isSelf` flag flips alignment, surface,
 * and the sender label ("You" instead of senderName).
 *
 * Status is rendered only on self bubbles:
 *   - pending → muted clock-ish dot
 *   - failed  → destructive refresh icon (retry affordance — wiring is future)
 *   - sent    → nothing (visually clean)
 *
 * Reactions (F5.4 / F7.4):
 *   - Long-press (mobile) or hover-button (desktop ≥ 900px via CSS) opens a
 *     <ReactionPopover>. The popover renders absolutely inside this row so a
 *     portal isn't needed for v1.
 *   - Existing reactions render via <ReactionRow> below the message text.
 *
 * Block (F9.1):
 *   - For non-self bubbles, the popover includes a Block icon. Tapping it
 *     opens a ConfirmDialog ("Block {handle}? You won't see their messages.").
 *     On confirm we call useIdentity().block(senderId), surface a toast, and
 *     the next render of <MessageList> filters this sender out.
 */
function Bubble({
  id,
  text,
  senderId,
  senderName,
  avatarSeed,
  sentAt,
  isSelf,
  status = 'sent',
  reactions,
  attachment,
  onCompareRequest,
}) {
  const { user, handle, block } = useIdentity();
  const { showToast } = useToast();
  const { prefill } = useComposerPrefill();
  const currentUserId = user || handle || '';

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const bubbleRef = useRef(null);

  // Reply: seed the composer with "@<sender> " and focus it. Falls back to
  // the senderName-vs-handle the bubble already shows. For self bubbles this
  // would prefill the user's own handle; we skip those (the popover still
  // renders Reply but it's a harmless no-op there).
  const handleReply = useCallback(() => {
    if (isSelf) return;
    const target = senderName || senderId || '';
    if (target) prefill(target);
  }, [isSelf, senderName, senderId, prefill]);

  const openPopover = useCallback(() => {
    if (status === 'pending' || status === 'failed') return;
    setPopoverOpen(true);
  }, [status]);

  const closePopover = useCallback(() => setPopoverOpen(false), []);

  // useLongPress returns event handlers + a `didLongPressRef`. The ref is for
  // consumers who want to short-circuit onClick after a long-press fires; we
  // don't need it here, but we MUST strip it before spreading onto the DOM —
  // React would otherwise warn about an unknown attribute.
  // eslint-disable-next-line no-unused-vars
  const { didLongPressRef: _ignoredLpRef, ...longPress } = useLongPress(
    openPopover,
    { delay: 500 },
  );

  const handleToggle = useCallback(
    (emoji) => {
      if (!id || !currentUserId) return;
      // Fire-and-forget. The snapshot stream will reflect the change. Errors
      // (e.g. cap-at-4) are swallowed in v1 — the chip simply won't appear.
      toggleReaction(id, emoji, currentUserId).catch(() => {});
    },
    [id, currentUserId],
  );

  const requestBlock = useCallback(() => {
    setBlockConfirmOpen(true);
  }, []);

  const confirmBlock = useCallback(() => {
    if (senderId) {
      block(senderId);
      showToast(`Blocked ${senderName || 'user'}`);
    }
    setBlockConfirmOpen(false);
  }, [block, senderId, senderName, showToast]);

  const cancelBlock = useCallback(() => setBlockConfirmOpen(false), []);

  const sideClass = isSelf ? 'self' : 'other';
  const bubbleClass =
    `glacier-bubble ${sideClass}` +
    (status === 'pending' ? ' glacier-bubble--pending' : '');

  // Only render the row if at least one emoji has reactors.
  const hasReactions =
    reactions &&
    Object.values(reactions).some((arr) => Array.isArray(arr) && arr.length > 0);

  return (
    <div className={`glacier-row ${sideClass}`} data-testid="bubble">
      <div className="glacier-row__avatar">
        <AnimalAvatar seed={avatarSeed || senderName || 'anon'} size={28} />
      </div>
      <div className="glacier-row__col glacier-row__col--reactable">
        <div
          ref={bubbleRef}
          className={bubbleClass}
          {...longPress}
        >
          <span className={`glacier-bubble__sender ${sideClass}`}>
            {isSelf ? 'You' : senderName || 'anon'}
          </span>
          {text && <span className="glacier-bubble__text">{text}</span>}
          {attachment && attachment.kind === 'code' && (
            <div className="glacier-bubble__attachment">
              <CodeBubble
                lang={attachment.lang}
                title={attachment.title}
                text={attachment.text}
                via={attachment.via}
                defaultCollapsed
                onCompareRequest={onCompareRequest}
              />
            </div>
          )}
          {attachment && attachment.kind === 'file' && (
            <div className="glacier-bubble__attachment">
              <FileAttachment
                attachment={attachment}
                currentUserId={currentUserId}
              />
            </div>
          )}
          <span className="glacier-bubble__time">{formatTime(sentAt)}</span>
          {isSelf && status === 'pending' && (
            <span
              className="glacier-bubble__status"
              aria-label="Sending"
              role="status"
            >
              <Icon name="dot" size={10} aria-hidden />
            </span>
          )}
          {isSelf && status === 'failed' && (
            <span
              className="glacier-bubble__status failed"
              aria-label="Failed to send, tap to retry"
              role="status"
            >
              <Icon name="refresh" size={12} aria-hidden />
            </span>
          )}
          {/* Desktop reveal trigger — CSS reveals it on bubble :hover at
              ≥ 900px. On mobile it's display:none and the long-press path
              handles it. */}
          <button
            type="button"
            className="glacier-bubble__react-trigger"
            aria-label="Add reaction"
            aria-haspopup="menu"
            aria-expanded={popoverOpen || undefined}
            onClick={openPopover}
            tabIndex={-1}
          >
            <Icon name="smile" size={14} aria-hidden />
          </button>
        </div>
        {hasReactions && (
          <ReactionRow
            reactions={reactions}
            currentUserId={currentUserId}
            onToggle={handleToggle}
            variant={sideClass}
          />
        )}
        {popoverOpen && (
          <ReactionPopover
            anchorRef={bubbleRef}
            variant={sideClass}
            onSelect={handleToggle}
            onDismiss={closePopover}
            onReply={handleReply}
            onBlock={isSelf ? undefined : requestBlock}
          />
        )}
        <ConfirmDialog
          isOpen={blockConfirmOpen}
          title="Block this user?"
          body={`You won't see ${senderName || 'their'} messages anymore. You can unblock from the user menu.`}
          confirmLabel="Block"
          confirmKind="destructive"
          onConfirm={confirmBlock}
          onCancel={cancelBlock}
        />
      </div>
    </div>
  );
}

export default Bubble;
export { formatTime as __formatTime };
